import { ChangeDetectorRef, Component, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs';
import { SAUModalService } from '@some-angular-utils/modal';
import packageInfo from '../../package.json';
import { AuthenticationService } from '@/apiConnection/authentication.service';
import { Permission } from '@/apiConnection/model/user';
import { environment } from '../../environments/environment';
import { SyncStatusBadgeComponent } from '@/components/sync-status/sync-status-badge.component';
import { DocumentDeviceAlertComponent } from '@/components/document-device-alert/document-device-alert.component';
import { ProfileFormComponent } from '@/components/profile-form/profile-form.component';
import { BusinessType, CompanyBusinessTypeService } from '@/apiConnection/company-business-type.service';

interface SubMenuItem {
  url: string;
  name: string;
  docType?: string;
  action?: keyof Permission;
  /** Hidden when the active company has this business_type. */
  hiddenForBusinessType?: BusinessType;
}

interface MenuItem {
  icon: string;
  url: string;
  name: string;
  panel: string;
  subMenu?: SubMenuItem[];
  superadminOnly?: boolean;
  /** Only shown when the active company has this business_type. */
  businessType?: BusinessType;
}

@Component({
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  imports: [RouterModule, SyncStatusBadgeComponent, DocumentDeviceAlertComponent],
})
export class LayoutComponent {
  appVersion = signal(packageInfo.version);
  mobileMenuOpen = signal(false);
  isProduction = signal(environment.production);

  toggleMobileMenu() {
    this.mobileMenuOpen.update(open => !open);
  }

  closeMobileMenu() {
    this.mobileMenuOpen.set(false);
  }

  private allMenus: MenuItem[] = [
    { icon: 'users.svg',          url: 'clients',         name: 'Clientes',       panel: 'clients' },
    { icon: 'truck.svg',          url: 'providers',        name: 'Proveedores',    panel: 'providers' },
    { icon: 'folder.svg',         url: 'categories',       name: 'Familias',       panel: 'categories' },
    { icon: 'boxes-stacked.svg',  url: 'products',         name: 'Productos',      panel: 'products' },
    {
      icon: 'inform.svg', url: 'stock-report', name: 'Informe de Existencias', panel: 'products',
      subMenu: [
        { url: 'stock-report',                    name: 'Sin valorar' },
        { url: 'stock-report/cost',                name: 'Valorado (coste)' },
        { url: 'stock-report/medium-price',        name: 'Valorado (precio medio)' },
      ],
    },
    {
      icon: 'euro.svg', url: 'budgets', name: 'Presupuestos', panel: 'documents',
      subMenu: [
        { url: 'budgets',                                 name: 'Informe de Presupuestos' },
        { url: 'budgets/new/budget',                      name: 'Presupuestos',                             docType: 'budget',     action: 'can_create' },
        { url: 'budgets/accept-budget',                   name: 'Aceptar Presupuesto',                      docType: 'factura',    action: 'can_create' },
      ],
    },
    {
      icon: 'cash-register.svg', url: 'sales', name: 'Ventas', panel: 'documents',
      subMenu: [
        { url: 'sales/new/delivery-note',                 name: 'Albaranes',                                docType: 'albaran',    action: 'can_create' },
        { url: 'sales/new/invoice',                       name: 'Facturas',                                 docType: 'factura',    action: 'can_create' },
        { url: 'sales/new/refund',                        name: 'Devolución',                               docType: 'devolucion', action: 'can_create' },
        { url: 'sales/albaran/new',                       name: 'Facturar albaranes de crédito',            docType: 'factura',    action: 'can_create' },
        { url: 'sales/massive-invoices/new',              name: 'Facturar total de albaranes de crédito',   docType: 'factura',    action: 'can_create' },
        { url: 'sales/massive-invoices/new/dailly',       name: 'Facturar diaria de albaranes de crédito',  docType: 'factura',    action: 'can_create' },
        { url: 'sales/massive-invoices/new/default',      name: 'Facturar clientes varios',                 docType: 'factura',    action: 'can_create' },
      ],
    },
        {
      icon: 'inform.svg', url: 'sales-reports', name: 'Informes', panel: 'documents',
      subMenu: [
        { url: 'sales-reports',                           name: 'Informe de ventas' },
        { url: 'sales-reports/list-clients',              name: 'Informe por clientes',                             docType: 'budget',     action: 'can_create' },
        { url: 'sales-reports/list-products',             name: 'Informe por artículos',                            docType: 'factura',    action: 'can_create' },
        { url: 'sales-reports/list-delivery-notes',       name: 'Informe de albaranes pendientes facturar',         docType: 'factura',    action: 'can_create' },
        { url: 'sales-reports/list-invoices',             name: 'Informe de facturas',                              docType: 'factura',    action: 'can_create' },
      ],
    },
    {
      icon: 'shop-cart.svg', url: 'purchase', name: 'Compras', panel: 'purchases',
      subMenu: [
        { url: 'purchase',                       name: 'Listado de Compras' },
        { url: 'purchase/new/invoice',           name: 'Facturas',    action: 'can_create' },
        { url: 'purchase/new/delivery-note',     name: 'Albaranes',   action: 'can_create' },
        { url: 'purchase/new/refund',            name: 'Devolución',  action: 'can_create' },
      ],
    },
    {
      icon: 'cash-register.svg', url: 'tickets', name: 'Tickets', panel: 'tickets',
      subMenu: [
        { url: 'tickets',             name: 'Listado de Tickets' },
        { url: 'tickets/new',         name: 'Nuevo Ticket', action: 'can_create' },
        { url: 'tickets/restaurant',  name: 'Pedido de restaurante (mesas)', action: 'can_create', hiddenForBusinessType: 'store' },
      ],
    },
    { icon: 'euro.svg', url: 'cash-closings', name: 'Cierres de caja', panel: 'cash_closings', businessType: 'store' },
    { icon: 'add-card.svg', url: 'payment-methods', name: 'Formas de pago', panel: 'pay_methods' },
    { icon: 'plus.svg', url: 'measures', name: 'Medidas', panel: 'measurements' },
    { icon: 'folder.svg', url: 'product-types', name: 'Tipos de producto', panel: 'product_types' },
    { icon: 'table.svg', url: 'tables', name: 'Mesas', panel: 'tables' },
    { icon: 'plug.svg', url: 'integrations', name: 'Integraciones', panel: 'integrations' },
    { icon: 'cubes.svg', url: 'inventory-history', name: 'Historial de Inventario', panel: 'history_inventory' },
    { icon: 'inform.svg', url: 'activity-logs', name: 'Registro de actividad', panel: 'activity_logs', superadminOnly: true },
    { icon: 'euro.svg', url: 'verifactu', name: 'Facturas Verifactu', panel: 'documents' },
  ];

  get menus(): MenuItem[] {
    const allowedDocTypes = this.authenticationService.getAllowedDocumentTypes();
    return this.allMenus
      .filter(m => !m.superadminOnly || this.authenticationService.isSuperAdmin)
      .filter(m => !m.businessType || this.businessTypeService.businessType() === m.businessType)
      .filter(m => this.authenticationService.hasPanel(m.panel))
      .map(m => {
        if (!m.subMenu) return m;
        const filtered = m.subMenu.filter(s => {
          if (s.action && !this.authenticationService.hasPanel(m.panel, s.action)) return false;
          if (s.docType && allowedDocTypes !== null && !allowedDocTypes.includes(s.docType)) return false;
          if (s.hiddenForBusinessType && this.businessTypeService.businessType() === s.hiddenForBusinessType) return false;
          return true;
        });
        return { ...m, subMenu: filtered };
      });
  }

  get isAdmin(): boolean {
    return this.authenticationService.isAdmin;
  }

  /** True when admin or superadmin that has already selected a company */
  get canManageRoles(): boolean {
    if (this.authenticationService.isSuperAdmin) {
      return this.authenticationService.selectedCompanyId !== null;
    }
    return this.authenticationService.isAdmin;
  }

  get isSuperAdmin(): boolean {
    return this.authenticationService.isSuperAdmin;
  }

  get currentUserName(): string {
    return this.authenticationService.currentUserValue?.name ?? '';
  }

  get selectedCompanyName(): string | null {
    return this.authenticationService.selectedCompanyName;
  }

  switchCompany() {
    this.authenticationService.clearCompanySelection();
    // SuperadminGuard will redirect to /companies on next data route attempt
  }

  private sauModalService = inject(SAUModalService);
  private cdr = inject(ChangeDetectorRef);

  private businessTypeService = inject(CompanyBusinessTypeService);

  constructor(private authenticationService: AuthenticationService) {
    // A superadmin can switch companies without the layout being recreated, so re-check on
    // every navigation (load() is a no-op while the company context is unchanged).
    this.businessTypeService.load();
    inject(Router).events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.businessTypeService.load());
  }

  logout() {
    this.authenticationService.logout();
  }

  openProfileForm() {
    const modalRef = this.sauModalService.open(ProfileFormComponent, {});

    // currentUserName reads a plain getter (not a signal), and the modal's save() updates it
    // from inside an HTTP subscribe callback - in this zoneless app that won't trigger CD on
    // its own, so force it here (same "detectChanges after an async response" pattern used
    // throughout the rest of the app).
    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.cdr.detectChanges();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }
}
